/**
 * Technique shortlist from on-device pose (upload journey).
 *
 * When a video is dropped on the upload page the browser extracts MediaPipe pose at the split
 * cadence (10 frames a second). From those landmarks this module suggests a SHORTLIST of the
 * techniques the clip most likely shows, to sit above the full technique list. It is a suggestion
 * only: never a metric, never sent to the server, and the user still picks.
 *
 * How it decides, in two independent reads of the same landmarks:
 *   1. the CAMERA: how wide the shoulder line is, in torso lengths, in the first second of the clip
 *      (the idea of IconicByMistakeDetectors.FrontOnShoulderWidthInTorsos), backed by the depth
 *      between the shoulders where the 2D read is undecided (as the phone app's
 *      validateFaceOnOrientation does);
 *   2. the FAMILY of movement: a handful of whole-clip measures in body units (is the body on the
 *      floor, do the hips travel, do the feet leave the ground, where do the hands go). Each family
 *      scores 0..1 and must clear FAMILY_FLOOR.
 * The shortlist is the winning family's techniques, the rows filmed from the detected angle first.
 *
 * What it does NOT do: tell near neighbours apart reliably (squat / deadlift / lunge, the yoga
 * holds, the dances). The shortlist exists for exactly that reason. And nothing here has seen a
 * real phone clip: every threshold was read off the repo's SYNTHETIC kinematic fixtures
 * (EL-Golf/El-Golf.UnitTest/Fixtures/*_synthetic.json) or off hand-built frames, and is marked
 * PROVISIONAL with where its value came from. A missing shortlist is better than a wrong one, so
 * every doubt resolves to abstaining.
 *
 * Coordinates: landmarks arrive normalised 0..1 against the video's own width and height. x and y
 * are therefore NOT comparable until multiplied back to pixels (a portrait clip squashes every
 * width against every height otherwise), and pixels are not comparable between clips, so every
 * measure below is in TORSO LENGTHS (shoulder midpoint to hip midpoint).
 */
import { ClientLandmark, FrameLandmarks, SPLIT_FPS } from "./clientPose";

export type TechniqueSuggestion = { sportId: number; why: string };
export type GuessInput = { frames: FrameLandmarks[]; width: number; height: number; durationSec: number };

/** "front" = the shoulder line square to the lens (face on, front or behind); "side" = in profile
 * (side view, down the line). */
export type CameraView = "front" | "side" | "unsure";

export type Family =
  | "floor" // lying, propped or inverted on the mat: push-up, plank, cobra, bridge, downward dog
  | "burpee" // down to the floor and back up, more than once
  | "hang" // both hands fixed overhead while the body travels: pull-up
  | "hold" // a standing pose held: Warrior II, Triangle, Tree, Chair
  | "squatLike" // hips lower and rise over planted feet, arms riding along: squat, deadlift, lunge
  | "jump" // both feet leave the ground: vertical jump, ollie, kickflip
  | "punch" // hands held up at the chin: jab-cross
  | "overheadStrike" // one hand reaching high overhead: tennis and volleyball serves
  | "throwOrShot" // the hands rising overhead together: free throw (pitch and bowling as neighbours)
  | "twoHandedSwing" // wrists together, travelling a long arc: golf, cricket batting
  | "dance" // a long, busy, front-view routine that nothing else explains
  | "unknown";

// ============================================================================ the catalogue

export type TechniqueRow = { sportId: number; technique: string; family: Family; view: "front" | "side" };

const row = (sportId: number, technique: string, family: Family, view: "front" | "side"): TechniqueRow => ({
  sportId,
  technique,
  family,
  view,
});

/**
 * The techniques this module can suggest: SportId (NetCoreInterfaces.SportId, the ids of
 * `SPORTS` in pages/Upload.tsx), the family that suggests it, and the camera its label names.
 * `view` follows the LABEL: Face On / Front View / Behind View are "front", Side View / Down the
 * Line are "side". techniqueGuess.test.ts reads the upload page's table and trips on drift.
 *
 * Within a family, techniques are listed commonest first; that is the default order of a shortlist.
 */
export const TECHNIQUE_TABLE: ReadonlyArray<TechniqueRow> = [
  // Two-handed swing.
  row(1, "golfSwing", "twoHandedSwing", "front"),
  row(3, "golfSwing", "twoHandedSwing", "side"),
  row(6, "golfChipping", "twoHandedSwing", "front"),
  row(7, "golfChipping", "twoHandedSwing", "side"),
  row(4, "golfPutting", "twoHandedSwing", "front"),
  row(5, "golfPutting", "twoHandedSwing", "side"),
  row(16, "cricketBatting", "twoHandedSwing", "front"),
  row(17, "cricketBatting", "twoHandedSwing", "side"),
  // One hand reaching high overhead. UNVALIDATED: there is no serve fixture; these ride on the
  // free throw's "hand above the head" read.
  row(8, "tennisServe", "overheadStrike", "front"),
  row(9, "tennisServe", "overheadStrike", "front"),
  row(2, "tennisServe", "overheadStrike", "side"),
  row(63, "volleyballServe", "overheadStrike", "front"),
  row(62, "volleyballServe", "overheadStrike", "side"),
  // Hands rising overhead together. Only the free throw has a fixture; the pitch and the bowl are
  // listed as its neighbours and are never the reason this family fires.
  row(52, "freeThrow", "throwOrShot", "front"),
  row(51, "freeThrow", "throwOrShot", "side"),
  row(65, "baseballPitch", "throwOrShot", "front"),
  row(64, "baseballPitch", "throwOrShot", "side"),
  row(19, "cricketBowling", "throwOrShot", "front"),
  row(18, "cricketBowling", "throwOrShot", "side"),
  // Hips lower and rise over planted feet.
  row(44, "squat", "squatLike", "front"),
  row(45, "squat", "squatLike", "front"), // behind: the shoulders are as square to the lens as from the front
  row(24, "squat", "squatLike", "side"),
  row(70, "deadlift", "squatLike", "front"),
  row(69, "deadlift", "squatLike", "side"),
  row(72, "lunge", "squatLike", "front"),
  row(71, "lunge", "squatLike", "side"),
  // Both feet leave the ground. The skate tricks carry no angle in their label; the SportId enum
  // calls them side view.
  row(48, "verticalJump", "jump", "front"),
  row(23, "verticalJump", "jump", "side"),
  row(36, "skateOllie", "jump", "side"),
  row(37, "skateKickflip", "jump", "side"),
  // Down to the floor and back up.
  row(75, "burpee", "burpee", "side"),
  // Both hands fixed overhead.
  row(73, "pullUp", "hang", "front"),
  row(74, "pullUp", "hang", "side"),
  // On the floor.
  row(67, "pushUp", "floor", "front"),
  row(66, "pushUp", "floor", "side"),
  row(55, "plank", "floor", "side"),
  row(58, "cobra", "floor", "side"),
  row(59, "bridge", "floor", "side"),
  row(47, "downwardDog", "floor", "front"),
  row(46, "downwardDog", "floor", "side"),
  // Standing holds.
  row(42, "warrior2", "hold", "front"),
  row(43, "warrior2", "hold", "side"),
  row(60, "triangle", "hold", "front"),
  row(61, "triangle", "hold", "side"),
  row(49, "tree", "hold", "front"),
  row(50, "tree", "hold", "side"),
  row(54, "chair", "hold", "front"),
  row(53, "chair", "hold", "side"),
  // Hands up at the chin.
  row(68, "jabCross", "punch", "front"),
  row(57, "jabCross", "punch", "side"),
  // Dance routines: all front view.
  row(30, "danceRenegade", "dance", "front"),
  row(31, "danceSavage", "dance", "front"),
  row(32, "danceLevitating", "dance", "front"),
  row(33, "danceToosie", "dance", "front"),
  row(34, "danceOutWest", "dance", "front"),
  row(35, "danceWap", "dance", "front"),
  row(76, "danceIconicByMistake", "dance", "front"),
];

/**
 * Techniques on the upload page that this version NEVER suggests (other than as the last-used
 * one), each with the reason. No fixture models them and no rule here was written for them:
 * inventing one would be a guess nobody could tell from a measurement.
 */
export const NOT_SUGGESTED: ReadonlyArray<{ sportId: number; why: string }> = [
  { sportId: 10, why: "Tennis forehand: a one-handed stroke; no fixture, no rule" },
  { sportId: 11, why: "Tennis forehand: a one-handed stroke; no fixture, no rule" },
  { sportId: 12, why: "Tennis forehand: a one-handed stroke; no fixture, no rule" },
  { sportId: 13, why: "Tennis backhand: no fixture, no rule (a two-hander would read as a swing)" },
  { sportId: 14, why: "Tennis backhand: no fixture, no rule (a two-hander would read as a swing)" },
  { sportId: 15, why: "Tennis backhand: no fixture, no rule (a two-hander would read as a swing)" },
  { sportId: 20, why: "Tenpin bowling: an underarm delivery off a walk-up; no fixture, no rule" },
  { sportId: 21, why: "Tenpin bowling: an underarm delivery off a walk-up; no fixture, no rule" },
  { sportId: 22, why: "Run gait: needs a stride read (and a treadmill run never travels); no fixture, no rule" },
  { sportId: 25, why: "Workplace lifting: no fixture; would read as a squat or a deadlift" },
  { sportId: 26, why: "Workplace push / pull: no fixture, no rule" },
  { sportId: 27, why: "Workplace overhead work: no fixture, no rule" },
  { sportId: 28, why: "Workplace repetitive task: no fixture, no rule" },
  { sportId: 29, why: "Workplace ladder use: no fixture, no rule" },
  { sportId: 38, why: "Skate drop-in: a crouch with no air; the body alone reads as a squat" },
  { sportId: 39, why: "Football penalty: a kick off a run-up; no fixture, no rule" },
  { sportId: 40, why: "Football free kick: a kick off a run-up; no fixture, no rule" },
  { sportId: 41, why: "Football corner: a kick off a run-up; no fixture, no rule" },
];

// ============================================================================ constants
//
// EVERY number below is PROVISIONAL. "Fixtures" means the 43 synthetic kinematic pose files under
// EL-Golf/El-Golf.UnitTest/Fixtures, sampled at 10 fps; the values quoted are what those files
// measure. None has been checked against a real clip.

/** PROVISIONAL (the brief; the phone app's validators use the same 0.5): a landmark below this
 * visibility is treated as not seen. A landmark with NO visibility counts as seen, as the pose
 * file defaults conf to 1. */
const MIN_VISIBILITY = 0.5;
/** PROVISIONAL: a WRIST is believed at a much lower visibility than the rest of the body. Filmed
 * from the side, one hand of a two-handed grip is hidden behind the other for the whole swing and
 * MediaPipe says so: on three real down-the-line golf swings (dev, 2026-09) both wrists cleared 0.5
 * in 0%, 6% and 24% of the frames, so the hands counted as never seen and nothing could be
 * suggested. Both clear 0.1 in 83% to 97% of the frames. The model still places the hidden wrist
 * (beside the one it can see), and every hand measure here is a robust percentile over the clip. */
const MIN_WRIST_VISIBILITY = 0.1;
/** PROVISIONAL (the brief: "about a third"): with a usable torso (both shoulders and both hips) in
 * fewer than this share of the frames there is no shortlist. */
const MIN_TRACKED_FRACTION = 1 / 3;
/** PROVISIONAL: a body part (the wrists, the ankles) seen in fewer than this share of the tracked
 * frames is treated as not seen at all. Chosen equal to MIN_TRACKED_FRACTION; no fixture hides a
 * part, so nothing measures it. */
const MIN_PART_FRACTION = 1 / 3;
/** PROVISIONAL: a family below this confidence is not suggested. Half way: every factor of a
 * family is a ramp between "plainly not" and "plainly so", and half is the middle of the ramp. */
const FAMILY_FLOOR = 0.5;
/** PROVISIONAL: the default shortlist length (the brief: "up to 4"). */
const DEFAULT_MAX = 4;

/** PROVISIONAL: travel and peaks are read between these percentiles, not min to max, so one
 * mis-tracked frame in twenty cannot make a range. */
const ROBUST_LOW = 0.05;
const ROBUST_HIGH = 0.95;
/** PROVISIONAL: the torso's true length is taken as this percentile of its length over the clip.
 * A torso only ever FORESHORTENS (a hinge toward the lens: the front-view deadlift's median torso
 * is 0.13 of the frame height against 0.16 standing), so the truth is near the longest seen; not
 * the maximum, to survive a jitter spike. */
const TORSO_UNIT_PERCENTILE = 0.9;

// ---- the camera

/** PROVISIONAL: the front-on shoulder line in torso lengths. The fixtures' generators draw 170 to
 * 180 px shoulders on a 300 px torso (0.57 to 0.60); the front-view fixtures measure 0.57 to 0.77.
 * (A real adult is likely wider, nearer 0.7 to 0.8: unmeasured here.) */
const FRONT_ON_SHOULDER_IN_TORSOS = 0.6;
/** PROVISIONAL: IconicByMistakeDetectors.TurnedShoulderLineInWidths (0.55 of the front-on width
 * means turned side-on; measured 0.23 to 0.49 side-on on its reference tutorial), restated in
 * torso lengths. Side-view fixtures measure 0 to 0.09; a boxer's bladed stance 0.27. */
const SIDE_MAX_SHOULDER_IN_TORSOS = 0.55 * FRONT_ON_SHOULDER_IN_TORSOS;
/** PROVISIONAL: IconicByMistakeDetectors.FrontAgainShoulderLineInWidths (0.75 of the front-on
 * width means facing front again), restated in torso lengths. Front-view fixtures measure 0.57+. */
const FRONT_MIN_SHOULDER_IN_TORSOS = 0.75 * FRONT_ON_SHOULDER_IN_TORSOS;
/** PROVISIONAL: the view is read on the first this-many tracked frames (1 s at the split cadence):
 * the address, before the shoulders turn. The hand-built swing addresses for 1.5 s; a face-on
 * dance fixture that ends on the floor (WAP) reads 0.60 here and 0.12 over the whole clip. */
const VIEW_EARLY_FRAMES = 10;
/** PROVISIONAL: the phone app's validateFaceOnOrientation accepts face-on at a shoulder
 * width-to-depth ratio above 2.0 (EL-App/app/validators/genericValidationHelpers.ts). Used only
 * where the 2D read is between the bands; the same ratio the other way round reads as side-on.
 * MediaPipe's z is in image WIDTHS, so depth is multiplied by the width before comparing. */
const DEPTH_DECIDES_RATIO = 2.0;
/** PROVISIONAL: depth is only believed when at least this share of the early frames carry it.
 * Unmeasured: the fixtures have no depth at all. */
const DEPTH_MIN_SHARE = 0.5;

// ---- on the floor

/** PROVISIONAL: a shoulder line longer than this many times the torso means the torso is pointing
 * at the lens (a push-up filmed from ahead of the hands measures 6.4; an upright front view never
 * exceeds 0.77). Set low in that gap because a real camera is never perfectly head-on. */
const FORESHORTENED_SHOULDERS_PER_TORSO = 1.5;
/** PROVISIONAL: ...and the legs point away from it too: hips to ankles shorter than this many
 * shoulder lines (the head-on push-up measures 0 to 0.44). A STANDING body whose torso tips toward
 * the lens (the bottom of a front squat, a side-view Triangle) keeps its legs in view at 1.17+
 * shoulder lines, and is not on the floor. With the ankles unseen the torso alone decides: a body
 * lying toward the lens hides its own feet. */
const FORESHORTENED_LEGS_PER_SHOULDER_LINE = 0.8;
/** PROVISIONAL: lying toward the lens for more than this share of the clip, the torso cannot be the
 * body unit and the shoulder line stands in (the head-on push-up: every frame; nothing else: none). */
const FORESHORTENED_CLIP_SHARE = 0.5;
/** PROVISIONAL: the body line (shoulders to ankles) further than this from vertical means lying or
 * propped on the floor. Push-up 69, plank 64, cobra 83, bridge 90 degrees; the deepest standing
 * lean in the fixtures (a side-view deadlift) is 3 degrees at the median, a squat 0. */
const LYING_BODY_LINE_DEG = 50;
/** PROVISIONAL: back within this of vertical is standing again (the hysteresis partner of
 * LYING_BODY_LINE_DEG when counting trips to the floor). Half of it; unmeasured. */
const UPRIGHT_BODY_LINE_DEG = 25;
/** PROVISIONAL: hips this far ABOVE the shoulders (torso lengths) is inverted: downward dog and
 * bridge both hold 0.69 to 0.74 of their fixture inverted. A tenth of a torso keeps a level
 * plank out. */
const INVERTED_HIPS_ABOVE_SHOULDERS = 0.1;
/** PROVISIONAL: share of the clip on the floor, from "not a floor technique" to "plainly one". The
 * floor fixtures spend 0.56 (plank, which walks in first) to 1.0 there; the burpee 0.24. */
const FLOOR_FRACTION: Band = [0.3, 0.55];
/** PROVISIONAL: more than this share inverted puts downward dog and bridge ahead of the prone
 * poses (they measure 0.69 to 0.74; the push-up, plank and cobra 0). */
const INVERTED_LEADS_FRACTION = 0.3;
/** PROVISIONAL: inverted with the hands no lower than this against the shoulders (torso lengths)
 * is a bridge: the shoulders are ON the floor, so nothing sits below them (the hands measure
 * -0.08). In downward dog the hands are the floor contact, far below the shoulders (-0.87 to -1.16). */
const BRIDGE_HANDS_UP = -0.35;
/** PROVISIONAL: trips between standing and the floor, from one visit (a plank: down, and perhaps
 * up) to a repeated one. The burpee fixture's three reps make six; four is two whole reps. */
const BURPEE_TRIPS: Band = [2, 4];
/** PROVISIONAL: a standing family loses confidence as the floor share rises through this band
 * (the standing fixtures measure 0; the burpee 0.24). */
const OFF_FLOOR_FRACTION: Band = [0.1, 0.3];

// ---- hips and feet

/** PROVISIONAL: vertical travel of the trunk (the hips or the shoulders, whichever moves further)
 * in torso lengths, from "standing" to "a rep". A hinge barely lowers the hips (deadlift 0.32 to
 * 0.39) but drops the shoulders 0.68 to 0.78; lunge 0.79+, squat 1.08+. A free throw's knee dip is
 * 0.16, a dance's bounce 0.23 at most. */
const TRUNK_TRAVEL: Band = [0.3, 0.5];
/** PROVISIONAL: hips that stay put (torso lengths of vertical travel): Tree 0.01 to 0.03, against
 * Warrior II sinking 0.81 into its stance. */
const HIP_TRAVEL: Band = [0.25, 0.4];
/** PROVISIONAL: travel of the LOWER ankle, in torso lengths, from "planted" to "airborne".
 * Vertical jump 0.72 to 0.74, ollie and kickflip 0.58; a front-view lunge (the lead foot stepping
 * toward the lens) 0.28; everything planted 0 to 0.06. A pull-up lifts the feet too (0.58 to
 * 0.76) and is kept out of the jump by its hands, not by this. */
const FEET_LIFT: Band = [0.3, 0.5];
/** PROVISIONAL: one ankle this much higher in the image than the other (torso lengths) is a foot
 * off the floor, or a deep stance toward the lens. Tree 1.3, Warrior II and Triangle seen from the
 * side 1.1; a front-view lunge peaks at 0.35. */
const ANKLE_LEVEL_GAP = 0.4;
/** PROVISIONAL: ankles this far apart (torso lengths) is a wide stance. Warrior II 1.89 and
 * Triangle 1.28 at the median; a squat or a free throw stands at 0.6 to 0.8. */
const WIDE_STANCE = 1.0;
/** PROVISIONAL: share of the clip a pose's signature must be present, from "passing through" to
 * "held". The hold fixtures keep their stance for 0.50 to 0.64 of a 7 s clip (Chair its raised
 * arms for 0.96); a dance passes through a wide stance or a lifted foot for 0.05 to 0.28 of its
 * routine. THIN: Warrior II's arms are level for only 0.39 to 0.41 of its fixture, the top of this
 * band, and Iconic by Mistake's wide stance (0.28) is inside it. */
const HELD_FRACTION: Band = [0.2, 0.4];
/** PROVISIONAL: Warrior II and Triangle share one stance; an arm raised to the vertical (Triangle's
 * top arm peaks at 1.33 to 1.46, Warrior II's level arms at 0 to 0.42) marks the other one down by
 * this much. Orders the pair; never gates the family. */
const HOLD_ARM_TIEBREAK = 0.9;
/** PROVISIONAL: a lunge's stance opens and closes by this much (torso lengths; the side-view lunge
 * measures 0.9, a squat or deadlift 0.1 to 0.4). Orders the lunge first; never gates a family. */
const LUNGE_STANCE_RANGE = 0.5;
/** PROVISIONAL: a hinge. Hips that drop less than this (deadlift 0.39; lunge 0.79+, squat 1.08+)
 * while the shoulders drop at least HINGE_SHOULDER_PER_HIP times as far (deadlift 1.7 to 2.5; squat
 * and lunge 1.1 to 1.6) order the deadlift first; never gates a family. */
const HINGE_MAX_HIP_TRAVEL = 0.6;
const HINGE_SHOULDER_PER_HIP = 1.65;

// ---- hands (heights are ABOVE the shoulder line, in torso lengths; the hips are at -1)

/** PROVISIONAL: both wrists this far above the shoulder line is "hands overhead" (the nose is
 * about 0.37 up). Pull-up 0.73 to 0.88 and Chair 1.02 to 1.10 at the median; a boxing guard 0.27. */
const OVERHEAD_UP = 0.5;
/** PROVISIONAL: share of the clip with both hands overhead, for a hang (pull-up 0.76 to 0.88). */
const HANG_OVERHEAD_FRACTION: Band = [0.5, 0.8];
/** PROVISIONAL: a hang's shoulders travel this far (pull-up 0.61 to 0.67 torso lengths)... */
const HANG_SHOULDER_TRAVEL: Band = [0.2, 0.4];
/** ...while the hands stay put: wrist travel as a share of shoulder travel (pull-up 0 to 0.05;
 * Chair pose, whose hands rise with the arms, 1.4). PROVISIONAL. */
const HANG_WRIST_PER_SHOULDER: Band = [0.3, 0.6];
/** PROVISIONAL: the higher wrist at or above this is an arm raised (level with the shoulders or
 * higher). Warrior II's arms are level (0 to 0.42 at the peak); hanging arms sit near -1. */
const ARM_RAISED_UP = -0.25;
/** PROVISIONAL: a guard: both wrists between these heights (jab-cross 0.20 to 0.34; hands at the
 * chest in a dance sit near -0.3, hands overhead above 0.7). */
const GUARD_UP: Band = [-0.15, 0.7];
/** PROVISIONAL: share of the clip in guard (jab-cross 0.89 to 0.91; nothing else above 0.34). */
const GUARD_FRACTION: Band = [0.5, 0.8];
/** PROVISIONAL: the higher wrist's peak, from "not overhead" to "above the head". Free throw 0.78;
 * the dances' arm shapes 0.04 to 0.66. */
const OVERHEAD_PEAK: Band = [0.5, 0.75];
/** PROVISIONAL: ...from hands that were NOT up for most of the clip (the higher wrist's median:
 * free throw 0.11 to 0.20; pull-up and Chair 0.88+). */
const OVERHEAD_MEDIAN: Band = [0.4, 0.6];
/** PROVISIONAL: at the peak the other hand is up too (free throw: -0.10 to -0.01, both hands on
 * the ball) or is not (a one-armed reach). Splits the shot from the strike; unvalidated for the
 * strike, which has no fixture. */
const BOTH_HANDS_PEAK_UP = -0.3;
/** PROVISIONAL: how much the less likely of the shot and the strike is marked down. */
const OVERHEAD_SIBLING = 0.8;
/** PROVISIONAL: wrists this close (torso lengths) are one grip. The hand-built golfer's are 0.1
 * apart; a front-view stander's hang 0.6+ apart. Every SIDE view also reads as "together" (one
 * wrist behind the other, 0.18 in the fixtures), which is why the arc below must also be there. */
const GRIP_GAP = 0.35;
/** PROVISIONAL: share of the clip the wrists are together, for a swing. Was [0.7, 0.9], off the synthetic swings;
 * real down-the-line clips read lower: the hidden wrist drifts past GRIP_GAP for a frame or two mid-swing and a clip
 * that runs on after the finish shows the hands let go (dev 20003: 0.77, 19999: 0.78, so nothing or a jab-cross was
 * offered and nothing was selected). Every real swing on dev reads 0.77 or more. A share alone never makes a swing:
 * dev 19981's jump reads 0.87 and stays a jump, its feet leave the ground (planted). */
const GRIP_FRACTION: Band = [0.6, 0.75];
/** PROVISIONAL: the grip's sideways travel about the shoulders (torso lengths). A hand-built full
 * swing 1.5 face on and 0.7 down the line, a chip 0.5; a free throw's hands go straight up (0.10
 * to 0.26). Was [0.3, 0.5]: a real down-the-line swing travels less across than the hand-built one (dev 19992: 0.38,
 * read as a held pose; the other real ones 0.55 to 0.62), so the band starts just above the free throw's 0.26. */
const SWING_ARC_ACROSS: Band = [0.27, 0.35];
/** PROVISIONAL: the grip's vertical travel (full swing 1.3, chip 0.45; a side-view deadlift's
 * hands slide 0.03). */
const SWING_ARC_UP: Band = [0.15, 0.3];
/** PROVISIONAL: the grip is low at address: its LOWEST height in the clip (address is at -1.0; a
 * guard never drops below +0.3). The lowest, not the median: a real clip is cut around the swing
 * and the golfer holds the finish, so the hands spend most of it up. On seven real down-the-line
 * swings (dev, 2026-10) the four whose wrists were tracked had a median grip of +0.38 to +0.52
 * above the shoulders, and the median rule turned every one of them away. */
const SWING_ADDRESS_UP: Band = [-0.3, -0.6];
/** PROVISIONAL: a FULL swing takes the grip to here or higher (the hand-built swing peaks at
 * +0.3, the chip at -0.5). Below it, chipping and putting lead. */
const FULL_SWING_PEAK_UP = -0.3;
/** PROVISIONAL: arms that ride with the body: either wrist's travel about the shoulders (torso
 * lengths). Squat and lunge 0 to 0.04, deadlift 0.27 to 0.43; a dance 1.3+, a jump's arm swing 2.2. */
const ARMS_QUIET: Band = [0.5, 0.9];
/** PROVISIONAL: arms that are the movement (dances 1.3 to 2.0). */
const ARMS_BUSY: Band = [0.8, 1.2];
/** PROVISIONAL: feet that are the movement: the stance opening and closing (Toosie Slide 0.78,
 * whose arms hang still) or the hips travelling sideways (0.87). */
const FEET_BUSY: Band = [0.5, 0.75];

// ---- dance

/** PROVISIONAL: clip length, from "could be anything" to "a routine". The dance fixtures run 13 to
 * 23 s (Toosie Slide 7 s); every rep and hold fixture is 8 s or shorter. */
const DANCE_SECONDS: Band = [8, 12];
/** PROVISIONAL: a busy front-view clip SHORTER than a routine is still, weakly, a dance when no
 * other family explains it: just above FAMILY_FLOOR, below every family with a signature. */
const DANCE_SHORT_CLIP_CONFIDENCE = 0.6;
/** PROVISIONAL: a dance is mostly danced standing (WAP, the one routine with floor work, spends
 * 0.40 of its fixture there). */
const DANCE_FLOOR_FRACTION: Band = [0.5, 0.8];
/** PROVISIONAL: this share of a dance on the floor is floor work, which only WAP has (0.40; the
 * other six routines 0). */
const FLOOR_WORK_FRACTION = 0.2;
/**
 * PROVISIONAL: how long each routine runs, in seconds: the length of its synthetic fixture, which
 * its generator describes as "the routine at real speed". Orders the dances by how close the clip's
 * length is. CIRCULAR on the fixtures (they ARE these lengths), and a real clip carries lead-in
 * and lead-out, so this is the weakest read in the module.
 */
const ROUTINE_SECONDS: { [technique: string]: number } = {
  danceRenegade: 15.6,
  danceSavage: 13,
  danceLevitating: 13,
  danceToosie: 7,
  danceOutWest: 13.2,
  danceWap: 13,
  danceIconicByMistake: 22.8,
};

// ============================================================================ geometry

type Band = [number, number];
type Pt = { x: number; y: number; z?: number };

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
/** 0 at lo, 1 at hi, linear between (hi below lo gives a falling ramp). */
const ramp = (v: number, band: Band) => clamp01((v - band[0]) / (band[1] - band[0]));
const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const s = values.slice().sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.max(0, Math.round(p * (s.length - 1))))];
}
const median = (values: number[]) => {
  if (values.length === 0) return 0;
  const s = values.slice().sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 === 1 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const travel = (values: number[]) => percentile(values, ROBUST_HIGH) - percentile(values, ROBUST_LOW);
const share = (flags: boolean[]) => (flags.length === 0 ? 0 : flags.filter(Boolean).length / flags.length);

const LEFT_SHOULDER = 11, RIGHT_SHOULDER = 12, LEFT_WRIST = 15, RIGHT_WRIST = 16;
const LEFT_HIP = 23, RIGHT_HIP = 24, LEFT_ANKLE = 27, RIGHT_ANKLE = 28;

/** One tracked frame, in PIXELS. */
type Tracked = {
  leftShoulder: Pt;
  rightShoulder: Pt;
  shoulders: Pt; // midpoint
  hips: Pt; // midpoint
  torso: number; // shoulder midpoint to hip midpoint
  shoulderLine: number; // length of the shoulder line
  wrists?: { left: Pt; right: Pt };
  ankles?: { left: Pt; right: Pt };
};

function trackedFrames(input: GuessInput): Tracked[] {
  const w = input.width, h = input.height;
  const out: Tracked[] = [];
  if (!(w > 0) || !(h > 0)) return out;
  (input.frames ?? []).forEach((frame) => {
    const byIndex: { [index: number]: ClientLandmark } = {};
    (frame?.landmarks ?? []).forEach((l) => (byIndex[l.index] = l));
    const at = (index: number, minVisibility = MIN_VISIBILITY): Pt | undefined => {
      const l = byIndex[index];
      if (!l || !Number.isFinite(l.x) || !Number.isFinite(l.y)) return undefined;
      if ((l.visibility ?? 1) < minVisibility) return undefined;
      const p: Pt = { x: l.x * w, y: l.y * h };
      if (typeof l.z === "number" && Number.isFinite(l.z)) p.z = l.z * w;
      return p;
    };
    const ls = at(LEFT_SHOULDER), rs = at(RIGHT_SHOULDER), lh = at(LEFT_HIP), rh = at(RIGHT_HIP);
    if (!ls || !rs || !lh || !rh) return;
    const shoulders = mid(ls, rs), hips = mid(lh, rh);
    const torso = dist(shoulders, hips);
    const shoulderLine = dist(ls, rs);
    if (!(torso > 0) && !(shoulderLine > 0)) return;
    const t: Tracked = { leftShoulder: ls, rightShoulder: rs, shoulders, hips, torso, shoulderLine };
    const lw = at(LEFT_WRIST, MIN_WRIST_VISIBILITY), rw = at(RIGHT_WRIST, MIN_WRIST_VISIBILITY);
    const la = at(LEFT_ANKLE), ra = at(RIGHT_ANKLE);
    if (lw && rw) t.wrists = { left: lw, right: rw };
    if (la && ra) t.ankles = { left: la, right: ra };
    out.push(t);
  });
  return out;
}

/** Lying along the lens's own axis: the torso, and the legs if they are seen, both collapse. */
const isForeshortened = (t: Tracked) =>
  t.shoulderLine > FORESHORTENED_SHOULDERS_PER_TORSO * t.torso &&
  (!t.ankles || dist(t.hips, mid(t.ankles.left, t.ankles.right)) < FORESHORTENED_LEGS_PER_SHOULDER_LINE * t.shoulderLine);

/**
 * The clip's body unit in pixels: the torso's length. When the torso points at the lens for most
 * of the clip (a floor technique filmed from the head end) its image length says nothing, and the
 * shoulder line stands in for it.
 */
function bodyUnit(frames: Tracked[]): number {
  if (share(frames.map(isForeshortened)) > FORESHORTENED_CLIP_SHARE) {
    return median(frames.map((t) => t.shoulderLine)) / FRONT_ON_SHOULDER_IN_TORSOS;
  }
  return percentile(frames.map((t) => t.torso), TORSO_UNIT_PERCENTILE);
}

// ============================================================================ the camera

function viewOf(frames: Tracked[], unit: number): CameraView {
  if (frames.length === 0 || !(unit > 0)) return "unsure";
  const early = frames.slice(0, VIEW_EARLY_FRAMES);
  const across = median(early.map((t) => Math.abs(t.leftShoulder.x - t.rightShoulder.x)));
  const ratio = across / unit;
  if (ratio >= FRONT_MIN_SHOULDER_IN_TORSOS) return "front";
  if (ratio <= SIDE_MAX_SHOULDER_IN_TORSOS) return "side";
  // Between the bands: the depth between the shoulders, where the model gave one.
  const deep = early
    .filter((t) => typeof t.leftShoulder.z === "number" && typeof t.rightShoulder.z === "number")
    .map((t) => Math.abs((t.leftShoulder.z as number) - (t.rightShoulder.z as number)));
  if (deep.length < early.length * DEPTH_MIN_SHARE) return "unsure";
  const depth = median(deep);
  if (across >= DEPTH_DECIDES_RATIO * depth) return "front";
  if (depth >= DEPTH_DECIDES_RATIO * across) return "side";
  return "unsure";
}

/** Which way the camera faces the athlete, read at the start of the clip. */
export function cameraView(input: GuessInput): CameraView {
  const frames = trackedFrames(input);
  if (!enoughTracked(input, frames)) return "unsure";
  return viewOf(frames, bodyUnit(frames));
}

function enoughTracked(input: GuessInput, frames: Tracked[]): boolean {
  const total = (input.frames ?? []).length;
  return total > 0 && frames.length > 0 && frames.length >= total * MIN_TRACKED_FRACTION;
}

// ============================================================================ the measures

/** Whole-clip measures. Lengths in torso lengths, heights ABOVE the shoulder line, shares 0..1. */
type Measures = {
  view: CameraView;
  durationSec: number;
  // the floor
  floorShare: number;
  invertedShare: number;
  floorTrips: number;
  // hips, shoulders, feet (the feet measures are all 0 when the ankles are not seen)
  hipTravelUp: number;
  hipTravelAcross: number;
  shoulderTravelUp: number;
  feetLift: number;
  ankleLevelGapShare: number;
  wideStanceShare: number;
  stanceRange: number;
  // hands
  handsSeen: boolean;
  bothOverheadShare: number;
  armRaisedShare: number;
  guardShare: number;
  gripShare: number;
  highWristPeak: number;
  highWristMedian: number;
  lowWristPeak: number;
  gripPeak: number;
  gripMedian: number;
  /** The grip at its lowest (robust): where it sits at address. */
  gripLow: number;
  gripArcAcross: number;
  gripArcUp: number;
  wristTravel: number;
  wristTravelUpInImage: number;
};

function measure(input: GuessInput, frames: Tracked[]): Measures {
  const u = bodyUnit(frames);
  const view = viewOf(frames, u);

  // ---- the floor
  const bodyLineDeg = (t: Tracked) => {
    const foot = t.ankles ? mid(t.ankles.left, t.ankles.right) : t.hips;
    return (Math.atan2(Math.abs(t.shoulders.x - foot.x), foot.y - t.shoulders.y) * 180) / Math.PI;
  };
  const lines = frames.map(bodyLineDeg);
  const inverted = frames.map((t) => t.hips.y < t.shoulders.y - INVERTED_HIPS_ABOVE_SHOULDERS * u);
  const onFloor = frames.map((t, i) => lines[i] > LYING_BODY_LINE_DEG || inverted[i] || isForeshortened(t));
  let trips = 0;
  let down: boolean | undefined;
  lines.forEach((deg, i) => {
    const now = onFloor[i] ? true : deg < UPRIGHT_BODY_LINE_DEG ? false : undefined;
    if (now === undefined) return;
    if (down !== undefined && now !== down) trips++;
    down = now;
  });

  // ---- feet
  // A part seen in too few frames is treated as not seen: its measures are all 0 ("planted, level,
  // hands at the shoulder line and still"). Every family that reads the hands needs one of those
  // measures well away from 0, so it scores nothing without them; no separate gate is needed.
  const withFeet = frames.filter((t) => t.ankles);
  const feet = withFeet.length >= frames.length * MIN_PART_FRACTION ? withFeet : [];
  const lowerAnkle = feet.map((t) => Math.max(t.ankles!.left.y, t.ankles!.right.y) / u);
  const stance = feet.map((t) => dist(t.ankles!.left, t.ankles!.right) / u);

  // ---- hands
  const withHands = frames.filter((t) => t.wrists);
  const hands = withHands.length >= frames.length * MIN_PART_FRACTION ? withHands : [];
  const handsSeen = hands.length > 0;
  const up = (t: Tracked, p: Pt) => (t.shoulders.y - p.y) / u;
  const high = hands.map((t) => Math.max(up(t, t.wrists!.left), up(t, t.wrists!.right)));
  const low = hands.map((t) => Math.min(up(t, t.wrists!.left), up(t, t.wrists!.right)));
  const grip = hands.map((t) => mid(t.wrists!.left, t.wrists!.right));
  const gripUp = hands.map((t, i) => up(t, grip[i]));
  const gripAcross = hands.map((t, i) => (grip[i].x - t.shoulders.x) / u);
  const about = (pick: (t: Tracked) => Pt) =>
    Math.hypot(
      travel(hands.map((t) => (pick(t).x - t.shoulders.x) / u)),
      travel(hands.map((t) => (pick(t).y - t.shoulders.y) / u))
    );

  return {
    view,
    durationSec: input.durationSec > 0 ? input.durationSec : (input.frames ?? []).length / SPLIT_FPS,
    floorShare: share(onFloor),
    invertedShare: share(inverted),
    floorTrips: trips,
    hipTravelUp: travel(frames.map((t) => t.hips.y / u)),
    hipTravelAcross: travel(frames.map((t) => t.hips.x / u)),
    shoulderTravelUp: travel(frames.map((t) => t.shoulders.y / u)),
    feetLift: travel(lowerAnkle),
    ankleLevelGapShare: share(feet.map((t) => Math.abs(t.ankles!.left.y - t.ankles!.right.y) / u >= ANKLE_LEVEL_GAP)),
    wideStanceShare: share(stance.map((s) => s >= WIDE_STANCE)),
    stanceRange: travel(stance),
    handsSeen,
    bothOverheadShare: share(low.map((v) => v >= OVERHEAD_UP)),
    armRaisedShare: share(high.map((v) => v >= ARM_RAISED_UP)),
    guardShare: share(low.map((v, i) => v >= GUARD_UP[0] && high[i] <= GUARD_UP[1])),
    gripShare: share(hands.map((t) => dist(t.wrists!.left, t.wrists!.right) / u <= GRIP_GAP)),
    highWristPeak: percentile(high, ROBUST_HIGH),
    highWristMedian: median(high),
    lowWristPeak: percentile(low, ROBUST_HIGH),
    gripPeak: percentile(gripUp, ROBUST_HIGH),
    gripMedian: median(gripUp),
    gripLow: percentile(gripUp, ROBUST_LOW),
    gripArcAcross: travel(gripAcross),
    gripArcUp: travel(gripUp),
    wristTravel: Math.max(about((t) => t.wrists!.left), about((t) => t.wrists!.right)),
    wristTravelUpInImage: Math.max(
      travel(hands.map((t) => t.wrists!.left.y / u)),
      travel(hands.map((t) => t.wrists!.right.y / u))
    ),
  };
}

// ============================================================================ the families

type Scored = { family: Exclude<Family, "unknown">; confidence: number; techniques: string[] };

/** Stable sort of a family's techniques by a score, highest first. */
function byScore(techniques: string[], score: { [technique: string]: number }): string[] {
  return techniques
    .map((t, i) => ({ t, i, s: score[t] ?? 0 }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((e) => e.t);
}

/** `first` moved to the front of `techniques`, the rest in their order. */
const leading = (first: string[], techniques: string[]) =>
  first.filter((t) => techniques.indexOf(t) >= 0).concat(techniques.filter((t) => first.indexOf(t) < 0));

/**
 * Every family's confidence for this clip, in PRIORITY order: where two tie, the earlier (the one
 * with the more specific signature) leads. Each confidence is the weakest of its conditions.
 */
function score(m: Measures): Scored[] {
  const standing = 1 - ramp(m.floorShare, OFF_FLOOR_FRACTION);
  const planted = 1 - ramp(m.feetLift, FEET_LIFT);
  const notOverhead = 1 - ramp(m.bothOverheadShare, HELD_FRACTION);

  // ---- floor
  const floor = ramp(m.floorShare, FLOOR_FRACTION);
  const onTheMat = ["pushUp", "plank", "cobra", "bridge", "downwardDog"];
  const shouldersDown = m.handsSeen && m.gripMedian >= BRIDGE_HANDS_UP;
  const floorOrder =
    m.invertedShare >= INVERTED_LEADS_FRACTION
      ? leading(shouldersDown ? ["bridge", "downwardDog"] : ["downwardDog", "bridge"], onTheMat)
      : onTheMat;

  // ---- burpee
  const burpee = ramp(m.floorTrips, BURPEE_TRIPS);

  // ---- hang: both hands overhead and still, the shoulders travelling beneath them
  const handsFixed =
    m.shoulderTravelUp > 0 ? 1 - ramp(m.wristTravelUpInImage / m.shoulderTravelUp, HANG_WRIST_PER_SHOULDER) : 0;
  const hang = Math.min(
    standing,
    ramp(m.bothOverheadShare, HANG_OVERHEAD_FRACTION),
    ramp(m.shoulderTravelUp, HANG_SHOULDER_TRAVEL),
    handsFixed
  );

  // ---- hold: a pose's signature present for a good share of the clip
  const hipsStill = 1 - ramp(m.hipTravelUp, HIP_TRAVEL);
  const oneFoot = Math.min(ramp(m.ankleLevelGapShare, HELD_FRACTION), hipsStill);
  const stance = Math.min(
    ramp(Math.max(m.wideStanceShare, m.ankleLevelGapShare), HELD_FRACTION),
    ramp(m.armRaisedShare, HELD_FRACTION)
  );
  const armsUp = Math.min(ramp(m.bothOverheadShare, HELD_FRACTION), 1 - handsFixed, planted);
  const hold = Math.min(standing, Math.max(oneFoot, stance, armsUp));
  const armVertical = m.highWristPeak >= OVERHEAD_PEAK[1];
  // Tree leads a tie with the stance poses: still hips is the more specific condition.
  const holdOrder = byScore(["tree", "warrior2", "triangle", "chair"], {
    tree: oneFoot,
    warrior2: stance * (armVertical ? HOLD_ARM_TIEBREAK : 1),
    triangle: stance * (armVertical ? 1 : HOLD_ARM_TIEBREAK),
    chair: armsUp,
  });

  // ---- squat-like: the trunk lowers and rises, the feet do not move, the arms ride along
  const armsQuiet = 1 - ramp(m.wristTravel, ARMS_QUIET);
  const trunkTravel = Math.max(m.hipTravelUp, m.shoulderTravelUp);
  const squatLike = Math.min(standing, planted, ramp(trunkTravel, TRUNK_TRAVEL), armsQuiet, notOverhead);
  const hinge = m.hipTravelUp < HINGE_MAX_HIP_TRAVEL && m.shoulderTravelUp >= HINGE_SHOULDER_PER_HIP * m.hipTravelUp;
  const lunging = m.stanceRange >= LUNGE_STANCE_RANGE;
  const squatOrder = leading(lunging ? ["lunge"] : hinge ? ["deadlift"] : [], ["squat", "deadlift", "lunge"]);

  // ---- jump
  const jump = Math.min(standing, ramp(m.feetLift, FEET_LIFT), notOverhead);

  // ---- punch: the hands live at the chin
  const punch = Math.min(standing, planted, ramp(m.guardShare, GUARD_FRACTION));

  // ---- a hand above the head, from hands that were down: the shot (together) and the strike (one arm)
  const levelFeet = 1 - ramp(m.ankleLevelGapShare, HELD_FRACTION);
  const narrowStance = 1 - ramp(m.wideStanceShare, HELD_FRACTION);
  const overhead = Math.min(
    standing,
    planted,
    levelFeet,
    narrowStance,
    ramp(m.highWristPeak, OVERHEAD_PEAK),
    1 - ramp(m.highWristMedian, OVERHEAD_MEDIAN)
  );
  const together = m.lowWristPeak >= BOTH_HANDS_PEAK_UP;
  const shot = overhead * (together ? 1 : OVERHEAD_SIBLING);
  const strike = overhead * (together ? OVERHEAD_SIBLING : 1);

  // ---- two-handed swing: one grip, low at address, travelling an arc about a trunk that stays put
  // (a side-view deadlift also shows one "grip" sliding against the shoulders; its trunk travels)
  const swing = Math.min(
    standing,
    planted,
    1 - ramp(trunkTravel, TRUNK_TRAVEL),
    ramp(m.gripShare, GRIP_FRACTION),
    ramp(m.gripArcAcross, SWING_ARC_ACROSS),
    ramp(m.gripArcUp, SWING_ARC_UP),
    ramp(m.gripLow, SWING_ADDRESS_UP)
  );
  // One grip swung through a big arc from a low address is a swing and nothing else. A real swing
  // also holds its finish with the hands up by the head, which on its own reads as a guard
  // (punch), raised arms (hold) or a shot — so those give way to exactly the extent this is a swing.
  // So does the dance fallback: a face-on swing in slow motion is a long, front-view clip with busy
  // arms (a real 18 s one scored dance 1.00 against swing 0.93), but a dancer's hands are apart
  // (together in 16% to 25% of three real routines, against 93%+ of every real swing).
  const notASwing = 1 - swing;
  const swingOrder =
    m.gripPeak >= FULL_SWING_PEAK_UP
      ? ["golfSwing", "golfChipping", "cricketBatting"] // a putt never takes the hands this high
      : ["golfChipping", "golfPutting", "golfSwing"];

  // ---- dance: front view, busy, mostly standing; the fallback for what nothing else explains
  const busy = Math.max(
    ramp(m.wristTravel, ARMS_BUSY),
    ramp(Math.max(m.stanceRange, m.hipTravelAcross), FEET_BUSY)
  );
  const dance =
    Math.min(m.view === "front" ? 1 : 0, busy, 1 - ramp(m.floorShare, DANCE_FLOOR_FRACTION)) *
    (DANCE_SHORT_CLIP_CONFIDENCE + (1 - DANCE_SHORT_CLIP_CONFIDENCE) * ramp(m.durationSec, DANCE_SECONDS));
  const routines = Object.keys(ROUTINE_SECONDS);
  const closeness: { [technique: string]: number } = {};
  routines.forEach((r) => (closeness[r] = -Math.abs(m.durationSec - ROUTINE_SECONDS[r])));
  const signature: string[] = [];
  if (m.floorShare >= FLOOR_WORK_FRACTION) signature.push("danceWap"); // the only routine with floor work
  if (ramp(m.wristTravel, ARMS_BUSY) === 0) signature.push("danceToosie"); // the only routine danced with the feet alone
  const danceOrder = leading(signature, byScore(routines, closeness));

  return [
    { family: "burpee", confidence: burpee, techniques: ["burpee"] },
    { family: "floor", confidence: floor, techniques: floorOrder },
    { family: "hang", confidence: hang, techniques: ["pullUp"] },
    { family: "hold", confidence: Math.min(hold, notASwing), techniques: holdOrder },
    { family: "jump", confidence: jump, techniques: ["verticalJump", "skateOllie", "skateKickflip"] },
    { family: "punch", confidence: Math.min(punch, notASwing), techniques: ["jabCross"] },
    { family: "twoHandedSwing", confidence: swing, techniques: swingOrder },
    { family: "squatLike", confidence: squatLike, techniques: squatOrder },
    {
      family: together ? "throwOrShot" : "overheadStrike",
      confidence: Math.min(together ? shot : strike, notASwing),
      techniques: together ? ["freeThrow", "baseballPitch", "cricketBowling"] : ["tennisServe", "volleyballServe"],
    },
    {
      family: together ? "overheadStrike" : "throwOrShot",
      confidence: Math.min(together ? strike : shot, notASwing),
      techniques: together ? ["tennisServe", "volleyballServe"] : ["freeThrow", "baseballPitch", "cricketBowling"],
    },
    { family: "dance", confidence: Math.min(dance, notASwing), techniques: danceOrder },
  ];
}

/** The families that clear the floor, most confident first (ties in priority order), with the view. */
function ranked(input: GuessInput): { view: CameraView; families: Scored[] } {
  const frames = trackedFrames(input);
  if (!enoughTracked(input, frames)) return { view: "unsure", families: [] };
  const m = measure(input, frames);
  const families = score(m)
    .map((s, i) => ({ s, i }))
    .filter((e) => e.s.confidence >= FAMILY_FLOOR)
    .sort((a, b) => b.s.confidence - a.s.confidence || a.i - b.i)
    .map((e) => e.s);
  return { view: m.view, families };
}

/** The family of movement the clip most likely shows, or "unknown" when none clears its floor. */
export function family(input: GuessInput): { family: Family; confidence: number } {
  const top = ranked(input).families[0];
  return top ? { family: top.family, confidence: top.confidence } : { family: "unknown", confidence: 0 };
}

// ============================================================================ the shortlist

const FAMILY_REASON: { [f in Exclude<Family, "unknown">]: string } = {
  floor: "On the floor",
  burpee: "Down to the floor and back up, repeatedly",
  hang: "Hanging from the hands",
  hold: "A held standing pose",
  squatLike: "Hips lower and rise over planted feet",
  jump: "Both feet leave the ground",
  punch: "Hands held up at the chin",
  overheadStrike: "One hand reaches high above the head",
  throwOrShot: "The hands rise above the head",
  twoHandedSwing: "Two-handed swing",
  dance: "A whole-body routine",
};

/** The reason shown on the shortlist entry that is there because it was the last technique used
 * on this device, not because the clip was seen to be it. */
export const LAST_USED_REASON = "Last used";

function reason(f: Exclude<Family, "unknown">, rowView: "front" | "side", view: CameraView): string {
  const base = FAMILY_REASON[f];
  if (view === "unsure" || rowView !== view) return base;
  if (view === "side") return `${base}, seen side-on`;
  return `${base}, seen ${f === "twoHandedSwing" ? "face-on" : "front-on"}`;
}

/**
 * One family's rows in suggestion order. With the camera known: every technique's rows filmed
 * from that angle first (in the family's technique order), then the other angle's. With the
 * camera unsure: technique by technique, each one's angles together.
 */
function rowsOf(s: Scored, view: CameraView): TechniqueRow[] {
  const rows: TechniqueRow[] = [];
  const of = (technique: string) => TECHNIQUE_TABLE.filter((r) => r.family === s.family && r.technique === technique);
  if (view === "unsure") {
    s.techniques.forEach((t) => of(t).forEach((r) => rows.push(r)));
    return rows;
  }
  s.techniques.forEach((t) => of(t).filter((r) => r.view === view).forEach((r) => rows.push(r)));
  s.techniques.forEach((t) => of(t).filter((r) => r.view !== view).forEach((r) => rows.push(r)));
  return rows;
}

/**
 * Up to `max` (default 4) techniques the clip most likely shows, best guess first, each with a
 * short human reason. `available` restricts the suggestions to those ids. `lastUsedSportId`, when
 * given and available, is included as "Last used" if it is not already a guess: it takes the last
 * slot and never displaces the top guess. `[]` when the pose is too thin or no family is confident
 * (just the last-used entry, when there is one).
 */
export function suggestTechniques(
  input: GuessInput,
  opts?: { max?: number; lastUsedSportId?: number | null; available?: ReadonlySet<number> }
): TechniqueSuggestion[] {
  const max = Math.max(0, Math.floor(opts?.max ?? DEFAULT_MAX));
  const allowed = (id: number) => !opts?.available || opts.available.has(id);
  if (max === 0) return [];

  const { view, families } = ranked(input);
  const list: TechniqueSuggestion[] = [];
  families.forEach((f) => {
    rowsOf(f, view).forEach((r) => {
      if (!allowed(r.sportId) || list.some((s) => s.sportId === r.sportId)) return;
      list.push({ sportId: r.sportId, why: reason(f.family, r.view, view) });
    });
  });

  const last = opts?.lastUsedSportId;
  const lastUsable = typeof last === "number" && Number.isFinite(last) && allowed(last);
  if (!lastUsable || list.slice(0, max).some((s) => s.sportId === last)) return list.slice(0, max);
  if (list.length === 0) return [{ sportId: last as number, why: LAST_USED_REASON }];
  if (max === 1) return list.slice(0, 1); // the one slot is the top guess's
  return list
    .filter((s) => s.sportId !== last)
    .slice(0, max - 1)
    .concat([{ sportId: last as number, why: LAST_USED_REASON }]);
}
